/**
 * Safely extract a human-readable message from an unknown catch value.
 * Use this in every catch (e: unknown) block instead of `e.message`.
 */
export function getErrorMessage(err: unknown): string {
  if (err instanceof Error) return err.message;
  if (typeof err === "string") return err;
  return "Unexpected error";
}

/**
 * Classify Supabase Edge Function errors into actionable categories.
 * Drives retry decisions (server/network → retry; validation/auth/rate-limit → fail-fast)
 * and user-facing copy. Promoted from inline `classifyFnError` in ContactFormSection (S44 S1 → S45 A2).
 */
export type FnErrorCategory =
  | "validation"
  | "auth"
  | "rate-limit"
  | "network"
  | "server"
  | "unknown";

export type RetryDecision = "retry" | "fail-fast";

interface FnLikeError {
  name?: string;
  context?: { status?: number };
}

export function classifyFnError(err: unknown): FnErrorCategory {
  if (!err || typeof err !== "object") return "unknown";
  const e = err as FnLikeError;
  if (e.name === "FunctionsFetchError" || e.name === "FunctionsRelayError") return "network";
  const status = e.context?.status;
  if (typeof status === "number") {
    if (status >= 500) return "server";
    if (status === 401 || status === 403) return "auth";
    if (status === 429) return "rate-limit";
    if (status >= 400) return "validation";
  }
  return "unknown";
}

export function classifyForRetry(err: unknown): RetryDecision {
  const cat = classifyFnError(err);
  return cat === "network" || cat === "server" ? "retry" : "fail-fast";
}
